import React from 'react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="container py-5">
    <div className="bg-white p-5 shadow rounded">

      <h1 className="mb-3 font-bold">Privacy Policy – DRIPSET</h1>
      <p className="mb-1"><span className="font-bold">Effective Date:</span> 23 May 2025</p>
      <p className="mb-1"><span className="font-bold">Last Updated:</span> 23 May 2025</p>
      <p className="mb-1"><span className="font-bold">Applies to:</span> DRIPSET Android App, iOS App, and Web Portal</p>
      <p className="mb-4"><span className="font-bold">Developer/Manufacturer:</span> Emsensing Technologies Private Limited</p>

      <h5 className="fw-semibold mt-4">1. Introduction</h5>
      <p>At <strong>Emsensing Technologies</strong>, we value your privacy. This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our DRIPSET mobile and web applications. The DRIPSET system is designed to monitor and control IV infusion parameters (e.g., drip rate, volume, drug delivery) and supports patient care by healthcare professionals.</p>
      <p>This policy complies with applicable data protection laws, including:</p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li><strong>Information Technology Act, 2000 (India) and Rules thereunder.</strong></li>
        <li><strong>General Data Protection Regulation (GDPR), for users in the EU.</strong></li>
        <li><strong>Apple App Store and Google Play Store privacy guidelines.</strong></li>
      </ul>

      <h5 className="fw-semibold mt-4">2. Information We Collect</h5>
      <p>We may collect the following types of data:</p>
      <p><strong>a) User Information</strong></p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>Full name (Patient/Nurse/Admin)</li>
        <li>Email ID</li>
        <li>Mobile number</li>
        <li>Role (Nurse/Admin)</li>
        <li>Hospital/Facility details</li>
        <li>Device ID</li>
        <li>Location</li>
      </ul>

      <p><strong>b) Device Data</strong></p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>Device ID and type</li>
        <li>WiFi/Bluetooth connection logs</li>
        <li>Battery status and uptime</li>
        <li>Location (connectivity zones)</li>
        <li>RPM, Total Quantity, Infusion Time, Current Time</li>
        <li>Infuse State, Remaining Saline, Saline Consumed</li>
        <li>ErrorCode, Battery, C_DC, R_DC</li>
      </ul>

      <p><strong>c) Infusion & Drug Delivery Data</strong></p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>Drip rate, infusion time, total quantity</li>
        <li>Alerts and events (e.g., air bubble, power failure)</li>
        <li>Drug details (name, batch, dosage, expiry)</li>
        <li>Patient ID</li>
      </ul>

      <p><strong>d) Technical & Usage Data</strong></p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>Log files</li>
        <li>Crash/error reports</li>
        <li>App usage patterns (buttons clicked, session durations)</li>
      </ul>

      <h5 className="fw-semibold mt-4">3. How We Use Your Information</h5>
      <p>We use your information to:</p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>Authenticate users and manage sessions securely.</li>
        <li>Enable nurses to monitor and control infusions in real-time.</li>
        <li>Log and visualize treatment data and alerts.</li>
        <li>Notify users of system alerts, errors, and infusion events.</li>
        <li>Generate audit logs and analytic reports for admins.</li>
        <li>Improve app performance and user experience.</li>
        <li>Comply with medical device and healthcare regulations.</li>
      </ul>

      <h5 className="fw-semibold mt-4">4. Legal Basis for Processing</h5>
      <p>We process data based on:</p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li><strong>Consent</strong> of the user (during registration and app use).</li>
        <li><strong>Legitimate interest</strong> in ensuring patient safety and device functionality.</li>
        <li><strong>Legal obligations</strong> for medical compliance and audit trails.</li>
      </ul>

      <h5 className="fw-semibold mt-4">5. Data Sharing and Disclosure</h5>
      <p>We do <strong>not</strong> sell or rent your personal information. We may share data:</p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>With <strong>authorized hospital administrators</strong> for operational oversight.</li>
        <li>With <strong>regulatory authorities</strong> (on lawful request).</li>
        <li>With <strong>cloud infrastructure providers</strong> (under strict confidentiality) to host secure backend services.</li>
        <li>With <strong>internal technical teams</strong> for maintenance and troubleshooting.</li>
      </ul>

      <h5 className="fw-semibold mt-4">6. Data Retention</h5>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>Data is retained only for as long as necessary for medical audit, compliance, and device operations.</li>
        <li>User and infusion logs are archived securely and purged periodically.</li>
        <li>You may request deletion of your account or data by contacting us.</li>
      </ul>

      <h5 className="fw-semibold mt-4">7. Data Security</h5>
      <p>We implement industry-standard security measures:</p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li><strong>End-to-end encryption</strong> for all data in transit.</li>
        <li><strong>Access control</strong> via role-based permissions.</li>
        <li><strong>Two-factor authentication</strong> for sensitive functions.</li>
        <li><strong>Audit logging</strong> for all device and user actions.</li>
        <li>Secure hosting with regular <strong>penetration testing and backups</strong>.</li>
      </ul>

      <h5 className="fw-semibold mt-4">8. Children’s Privacy</h5>
      <p>The <strong>DRIPSET</strong> app is not intended for use by individuals under the age of 18. No knowingly collected data from minors.</p>

      <h5 className="fw-semibold mt-4">9. Your Rights</h5>
      <p>You have the right to:</p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>Access and review your stored data.</li>
        <li>Correct or update your personal information.</li>
        <li>Request deletion of your account (subject to regulatory constraints).</li>
        <li>Withdraw consent at any time (may impact app functionality).</li>
      </ul>
      <p>Email us at <a href="mailto:technical@emsense.tech" className="text-decoration-none text-primary">technical@emsense.tech</a> to exercise your rights.</p>

      <h5 className="fw-semibold mt-4">10. Third-Party Services</h5>
      <p>The app may integrate with:</p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>Hospital EHR systems</li>
        <li>Secure cloud hosting providers (e.g., AWS, Azure)</li>
      </ul>
      <p>These services follow their own privacy standards. We ensure all integrations maintain data protection compliance.</p>

      <h5 className="fw-semibold mt-4">11. Cross-Border Data Transfer</h5>
      <p>If data is processed outside your jurisdiction (e.g., India or EU), we ensure compliance with applicable data protection laws through standard contractual clauses or data protection agreements.</p>

      <h5 className="fw-semibold mt-4">12. Updates to This Policy</h5>
      <p>We may update this Privacy Policy from time to time. Changes will be notified via:</p>
      <ul className="ps-3" style={{ listStyleType: 'disc' }}>
        <li>In-app messages</li>
        <li>Email (if provided)</li>
        <li>Updated version on our website</li>
      </ul>

      <h5 className="fw-semibold mt-4">13. Contact Us</h5>
      <div className="mb-4">
        <p className='mb-1'>For questions, feedback, or data concerns:</p>
        <p className="mb-1"><strong>Emsensing Technologies Pvt. Ltd.</strong></p>
        <p className="mb-1">AMTZ Campus, Visakhapatnam – 530031, India</p>
        <p className="mb-1">📧 <a href="mailto:technical@emsense.tech" className="text-decoration-none text-primary">technical@emsense.tech</a></p>
        <p>📞 <a href="tel:+918072011799" className="text-decoration-none text-primary">+91-8072011799</a></p>
      </div>

    </div>
  </div>
  );
};

export default PrivacyPolicy;
